/**
 * Supabase Document Repository
 * Handles RAG document management via Supabase Storage + Database + Edge Functions
 */

import { requireSupabaseClient } from '@/lib/supabase'
import type {
  IDocumentRepository,
  Document,
  UploadDocumentData,
} from '../interfaces/IDocumentRepository'

// Allowed file types
const ALLOWED_TYPES: Record<string, string> = {
  'text/csv': 'csv',
  'text/plain': 'txt',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
}

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

export class SupabaseDocumentRepository implements IDocumentRepository {
  /**
   * Upload a document for processing
   */
  async uploadDocument(data: UploadDocumentData): Promise<Document> {
    const supabase = requireSupabaseClient()

    // Get current user
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()
    if (authError || !user) throw new Error('Not authenticated')

    // Validate file type
    const fileType = ALLOWED_TYPES[data.file.type]
    if (!fileType) {
      throw new Error(
        `Unsupported file type: ${data.file.type}. Allowed: CSV, XLSX, PDF, DOCX`
      )
    }

    // Validate file size
    if (data.file.size > MAX_FILE_SIZE) {
      throw new Error(
        `File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`
      )
    }

    // Generate unique storage path
    const timestamp = Date.now()
    const safeName = data.file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const storagePath = `${user.id}/${timestamp}_${safeName}`

    // 1. Upload file to Storage
    const { error: uploadError } = await supabase.storage
      .from('rag-documents')
      .upload(storagePath, data.file, {
        contentType: data.file.type,
        upsert: false,
      })

    if (uploadError) {
      throw new Error(`Failed to upload file: ${uploadError.message}`)
    }

    // 2. Create document record
    const { data: doc, error: insertError } = await supabase
      .from('documents')
      .insert({
        user_id: user.id,
        filename: data.file.name,
        file_type: fileType,
        file_size: data.file.size,
        storage_path: storagePath,
        ticker: data.ticker?.toUpperCase() || null,
        document_type: data.documentType || 'ticker',
        status: 'processing',
      } as Record<string, unknown>)
      .select()
      .single()

    if (insertError || !doc) {
      // Cleanup uploaded file on failure
      await supabase.storage.from('rag-documents').remove([storagePath])
      throw new Error(
        `Failed to create document record: ${insertError?.message}`
      )
    }

    // 3. Trigger processing Edge Function (fire and forget)
    supabase.functions
      .invoke('process-document', {
        body: { documentId: doc.id },
      })
      .catch((err) => {
        console.error('Failed to trigger document processing:', err)
      })

    return this.mapToDocument(doc)
  }

  /**
   * Get all documents (admin view)
   */
  async getDocuments(): Promise<Document[]> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(`Failed to fetch documents: ${error.message}`)
    }

    return (data || []).map(this.mapToDocument)
  }

  /**
   * Get a single document by ID
   */
  async getDocument(documentId: string): Promise<Document | null> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('id', documentId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw new Error(`Failed to fetch document: ${error.message}`)
    }

    return data ? this.mapToDocument(data) : null
  }

  /**
   * Delete a document (removes file from storage + record cascades to chunks)
   */
  async deleteDocument(documentId: string): Promise<void> {
    const supabase = requireSupabaseClient()

    // 1. Get document to find storage path
    const { data: doc, error: fetchError } = await supabase
      .from('documents')
      .select('storage_path')
      .eq('id', documentId)
      .single()

    if (fetchError || !doc) {
      throw new Error(`Document not found: ${fetchError?.message}`)
    }

    // 2. Delete from storage
    const { error: storageError } = await supabase.storage
      .from('rag-documents')
      .remove([doc.storage_path])

    if (storageError) {
      console.error('Failed to delete file from storage:', storageError)
      // Continue to delete DB record anyway
    }

    // 3. Delete record (cascades to document_chunks)
    const { error: deleteError } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId)

    if (deleteError) {
      throw new Error(`Failed to delete document: ${deleteError.message}`)
    }
  }

  /**
   * Map database row to Document interface
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapToDocument(row: any): Document {
    return {
      id: row.id,
      userId: row.user_id,
      filename: row.filename,
      fileType: row.file_type,
      fileSize: row.file_size,
      storagePath: row.storage_path,
      ticker: row.ticker || null,
      documentType: row.document_type || 'ticker',
      status: row.status,
      errorMessage: row.error_message || null,
      chunkCount: row.chunk_count || 0,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }
  }
}
